/**
 * Shared mutable ref for the FACE animation blueprint graph state.
 * BlueprintEditor writes here when the "Face" blueprint is active.
 * HeadModel reads here every frame in useFrame().
 *
 * Mirrors graphState.js (body), kept separate to avoid any coupling.
 */
export const faceGraphState = {
  nodes: [],
  edges: [],
}

// Debug: expose on window so we can inspect from the console / Playwright
if (typeof window !== 'undefined') {
  window.__FACE_GRAPH_STATE__ = faceGraphState
}

// ── External-insert channel ──────────────────────────────────────────────────
// The editor's React state flows DOWN into faceGraphState; there is no reverse
// path. So a node pushed straight onto faceGraphState.nodes from outside the
// editor (the Rig Preview panel's "Save as pose") is invisible to the editor
// AND gets clobbered by its next down-sync. This channel closes that gap: it
// carries the inserted node so the editor merges exactly it into React state
// (append, dedup by id) — never a broad reconcile, which would be unsafe
// mid character-switch. Mirrors eventGraphState's channel deliberately.
const _insertSubs = new Set()
// Inserts that happened while no editor was mounted. Saving a pose is most
// natural with the editor CLOSED (you are looking at the avatar, not the
// graph), so a fire-and-forget notify would drop exactly the common case.
// Drained by the first subscriber to mount, then cleared — keeping them would
// resurrect a node the user had since deleted.
let _pendingInserts = []

/** Subscribe to external node inserts. Returns an unsubscribe function. */
export function subscribeFaceGraphInsert(cb) {
  _insertSubs.add(cb)
  if (_pendingInserts.length) {
    const queued = _pendingInserts
    _pendingInserts = []
    for (const node of queued) {
      try { cb(node) } catch (e) { console.warn('[faceGraphState] insert subscriber threw:', e) }
    }
  }
  return () => _insertSubs.delete(cb)
}

/** Announce a node inserted into the face graph from outside the editor. */
export function notifyFaceGraphInsert(node) {
  if (_insertSubs.size === 0) { _pendingInserts.push(node); return }
  for (const cb of _insertSubs) {
    try { cb(node) } catch (e) { console.warn('[faceGraphState] insert subscriber threw:', e) }
  }
}
